import React, { ChangeEvent, FC, ReactElement, useState } from 'react';
import { connect } from 'react-redux';
import isObject from 'lodash/isObject';
import pickBy from 'lodash/pickBy';
import { clsx } from 'clsx';
import moment from 'moment';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Theme } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TextField } from '@mui/material';
// Components
import Text from '../text';
import ModalButton, { ButtonType } from './templates/modalButton';
import ModalButtonContainer from './templates/modalButtonContainer';
import Space from '../layout/space';
import InputField from '../form/input';
import RadioLabel from '../form/radioLabel';
import DatePicker from '../datePicker/datePicker';
import EditSelect from '../profile/editSelect';
import SelectMultiple from '../form/selectMulti';
import SvgIcon from '../icons/SvgIcon';
import ExportButton from '../buttons/exportButton';
import Separator from '../layout/separator';
// Modules
import { selectors as pendingSelectors } from '../../core/modules/pendings';
// Resources
import { stringsRes } from '../../resources/strings';
import { PropsFromModal } from './createModalHOC';
import { FilterCategory } from '../listing';
import { addTimeToDate } from '../../utils/date';
import { asyncStorageApi, getUser, KEY_TABLE_FILTER } from '../../localStorageAPI';
import { SelectOption } from '../../core/apiTypes';
import { IconCommon } from '../../resources/images';
import { ToastWithIcon } from '../../navigation/routes/toastProvider';
import useGlobalStyles from '../hook/useGlobalStyles';
import { isEmpty } from '../../utils/basics';

const EXPORT_MIN_DATE = new Date('01/01/2022').getTime() / 1000;

interface FilterProps extends PropsFromModal {
    filters: FilterCategory[];
    description?: string;
    labelCancel?: string;
    typeCancel?: ButtonType;
    labelConfirm?: string;
    typeConfirm?: ButtonType;
    onConfirmAction?: (formattedDatas: any, datas: any) => void;
    onCancelAction?: (formattedDatas: any, datas: any) => void;
    onExportAction?: (formattedDatas: any, datas: any, real: boolean) => void;
    isExporting: boolean;
}

const Filter: FC<FilterProps> = (props): ReactElement => {
    const {
        onRequestClose,
        filters,
        description,
        labelCancel,
        typeCancel,
        labelConfirm,
        typeConfirm,
        onConfirmAction,
        onCancelAction,
        onExportAction,
        isExporting,
    } = props;
    const classes = useStyle();
    const globalClasses = useGlobalStyles();

    // Get localstorage filter
    const user = getUser();
    const [filterDatas, setFilterDatas] = useState<any>(
        asyncStorageApi.restoreData(KEY_TABLE_FILTER)?.[window.location.pathname]?.filterDatas || {},
    );

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let autoMaxTime = null;
        if (e.target.name.endsWith('_min') && moment(e.target.value).isValid()) {
            autoMaxTime = addTimeToDate(1, e.target.value, 'd');
        }

        console.log(e.target.name, e.target.value);

        setFilterDatas({
            ...filterDatas,
            [e.target.name]: e.target.value,
            ...(autoMaxTime && { [e.target.name.replace('_min', '_max')]: autoMaxTime }),
        });
    };

    const formatData = (): any => {
        const tranformFilters = filters
            .reduce((acc, item) => {
                return [...acc, ...item.filters];
            }, [])
            .filter((f) => f.asArray || f.type === 'Date' || f.type === 'Interval' || f.booleanValue);

        const possiblesValues = {};
        let formattedFilterDatas = { ...filterDatas };
        tranformFilters.forEach((f) => {
            const value = filterDatas[f.id];
            if (f.booleanValue && f.type === 'Boolean') {
                const data = f.booleanValue[value];
                if (Array.isArray(data)) {
                    formattedFilterDatas[f.id] = f.booleanValue[value];
                } else if (isObject(data) && !isEmpty(data)) {
                    formattedFilterDatas = { ...formattedFilterDatas, ...f.booleanValue[value] };
                }
            } else if (f.asArray && f.type === 'Multiple' && value && Array.isArray(value)) {
                formattedFilterDatas[f.id] = value.filter((v) => v?.id).map((v) => v.id);
                possiblesValues[f.id] = f.pickerOptions;
            } else if (f.asArray && !Array.isArray(value) && value) {
                formattedFilterDatas[f.id] = [value];
                possiblesValues[f.id] = f.pickerOptions;
            } else if (f.type === 'Interval') {
                if (filterDatas[`${f.id}_min`]) {
                    formattedFilterDatas[`${f.id}_min`] = moment(filterDatas[`${f.id}_min`]).unix();
                }
                if (filterDatas[`${f.id}_max`]) {
                    formattedFilterDatas[`${f.id}_max`] = moment(filterDatas[`${f.id}_max`]).endOf('day').unix();
                }
            } else if (value) {
                formattedFilterDatas[f.id] = moment(value).unix();
            }
        });
        formattedFilterDatas = pickBy(formattedFilterDatas);
        return { formattedFilterDatas, possiblesValues };
    };

    const onClickConfirm = (): void => {
        if (onConfirmAction) {
            const { formattedFilterDatas, possiblesValues } = formatData();
            const tablefilters = asyncStorageApi.restoreData(KEY_TABLE_FILTER) || {};
            asyncStorageApi.persistData(KEY_TABLE_FILTER, {
                ...tablefilters,
                [window.location.pathname]: { queryDatas: formattedFilterDatas, filterDatas, possiblesValues },
            });
            onConfirmAction(formattedFilterDatas, filterDatas);
        }
        onRequestClose();
    };

    const onClickCancel = (): void => {
        const tablefilters = asyncStorageApi.restoreData(KEY_TABLE_FILTER) || {};
        asyncStorageApi.persistData(KEY_TABLE_FILTER, { ...tablefilters, [window.location.pathname]: {} });
        if (onCancelAction) {
            onCancelAction({}, {});
        }
        onRequestClose();
    };

    const onClickExport =
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (real = false) =>
            (): void => {
                const { formattedFilterDatas } = formatData();
                if (!formattedFilterDatas.timestamp_min && !formattedFilterDatas.timestamp_max) {
                    ToastWithIcon('error', stringsRes.filter.errors.noInterval);
                } else if (formattedFilterDatas.timestamp_min < EXPORT_MIN_DATE) {
                    ToastWithIcon('error', stringsRes.filter.errors.before2022);
                } else {
                    onExportAction(formattedFilterDatas, filterDatas, real);
                }
            };

    return (
        <div className={classes.filterContainer}>
            <div className={globalClasses.flexCol}>
                <Text.Description16 customColor="grey" text={description ?? stringsRes.filter.description} />
            </div>
            <Space value={2} />
            <div className={globalClasses.flexCol}>
                {filters?.map((category, index) => {
                    return (
                        <React.Fragment key={category.title}>
                            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="frFR">
                                <Text.Description16 customColor="grey" text={category.title} />
                                <Space value={1} />
                                {category.filters?.map((filter) => {
                                    const key = filter.id;
                                    const name = filter.id;
                                    const value: any = filterDatas?.[filter.id] ?? '';
                                    const minValue = filterDatas?.[`${filter.id}_min`] ?? '';
                                    const maxValue = filterDatas?.[`${filter.id}_max`] ?? '';
                                    const placeholder = filter.placeholder ?? filter.label;
                                    const selectOptions = filter?.pickerOptions?.map((p) => ({
                                        id: p.value,
                                        label: p.label,
                                    }));
                                    const selectedOption = filter?.pickerOptions?.find(
                                        (option) => option.value === value,
                                    );

                                    switch (filter.type) {
                                        case 'String':
                                            return (
                                                <InputField
                                                    key={key}
                                                    name={name}
                                                    onChange={onChange}
                                                    className={classes.filterInput}
                                                    value={value}
                                                    label={placeholder}
                                                />
                                            );
                                        case 'Picker':
                                            return (
                                                <EditSelect
                                                    key={key}
                                                    name={name}
                                                    title={placeholder}
                                                    containerClassName={classes.filterInput}
                                                    noTitle
                                                    items={filter.pickerOptions ?? []}
                                                    initialValue={value}
                                                    onChange={onChange}
                                                />
                                            );
                                        case 'Autocomplete':
                                            return (
                                                <Autocomplete
                                                    key={key}
                                                    id={name}
                                                    title={placeholder}
                                                    disablePortal
                                                    classes={{ paper: classes.autoCompletePaper }}
                                                    className={clsx(classes.filterInput, classes.autoCompleteInput)}
                                                    options={filter.pickerOptions ?? []}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label={placeholder} variant="filled" />
                                                    )}
                                                    renderOption={(listProps, option) => (
                                                        <li {...listProps} key={option.value}>
                                                            {option.label}
                                                        </li>
                                                    )}
                                                    isOptionEqualToValue={(option, val) => option.value === val.value}
                                                    value={selectedOption || null}
                                                    defaultValue={selectedOption || null}
                                                    onChange={(_, val, reason) =>
                                                        onChange({
                                                            target: { value: val?.value, name, reason },
                                                        } as unknown as ChangeEvent<HTMLInputElement>)
                                                    }
                                                    popupIcon={
                                                        <div className={`material-icons ${classes.icon}`}>
                                                            <SvgIcon
                                                                icon={IconCommon.ChevronDown}
                                                                color="black"
                                                                fill="none"
                                                            />
                                                        </div>
                                                    }
                                                />
                                            );
                                        case 'Multiple':
                                            return (
                                                <SelectMultiple
                                                    key={key}
                                                    name={name}
                                                    options={selectOptions ?? []}
                                                    className={classes.multiple}
                                                    startValue={(value || null)?.map((v) => v?.id)?.join(',') || null}
                                                    label={placeholder}
                                                    onSelectChange={(vals?: SelectOption[]) =>
                                                        onChange({
                                                            target: { value: vals, name },
                                                        } as unknown as ChangeEvent<HTMLInputElement>)
                                                    }
                                                    fullWidth
                                                />
                                            );
                                        case 'Date':
                                            return (
                                                <DatePicker
                                                    key={key}
                                                    className={classes.filterInput}
                                                    noError
                                                    disablePast={false}
                                                    selectedDate={moment(value) || null}
                                                    onChangeDateTime={(date: any) =>
                                                        onChange({
                                                            target: { value: date, name },
                                                        } as unknown as ChangeEvent<HTMLInputElement>)
                                                    }
                                                    fullWidth
                                                />
                                            );
                                        case 'Interval':
                                            return (
                                                <div
                                                    key={key}
                                                    className={clsx(classes.filterInput, globalClasses.flexRow)}
                                                >
                                                    <DatePicker
                                                        className={classes.dateInterval}
                                                        selectedDate={moment(minValue) || null}
                                                        noError
                                                        disablePast={false}
                                                        label={stringsRes.common.startDateLabel}
                                                        onChangeDateTime={(date: any) =>
                                                            onChange({
                                                                target: { value: date, name: `${name}_min` },
                                                            } as unknown as ChangeEvent<HTMLInputElement>)
                                                        }
                                                        fullWidth
                                                    />
                                                    <DatePicker
                                                        className={classes.dateInterval}
                                                        selectedDate={moment(maxValue) || null}
                                                        noError
                                                        disablePast={false}
                                                        label={stringsRes.common.endDateLabel}
                                                        onChangeDateTime={(date: any) =>
                                                            onChange({
                                                                target: { value: date, name: `${name}_max` },
                                                            } as unknown as ChangeEvent<HTMLInputElement>)
                                                        }
                                                        fullWidth
                                                    />
                                                </div>
                                            );
                                        case 'Boolean':
                                            return (
                                                <RadioLabel
                                                    key={key}
                                                    name={name}
                                                    onChange={() =>
                                                        onChange({
                                                            target: { value: !value, name },
                                                        } as unknown as ChangeEvent<HTMLInputElement>)
                                                    }
                                                    containerClassName={classes.filterInput}
                                                    checked={value || false}
                                                    title={placeholder}
                                                />
                                            );
                                        default:
                                            return <div key={key}>{filter.label}</div>;
                                    }
                                })}
                                <Space value={2.5} />
                                {index < filters.length - 1 && (
                                    <>
                                        <Separator height={1} />
                                        <Space value={2.5} />
                                    </>
                                )}
                            </LocalizationProvider>
                        </React.Fragment>
                    );
                })}
            </div>
            <div className={classes.filterStickyContainer}>
                <ModalButtonContainer displayEnd>
                    <ModalButton
                        type={typeCancel ?? ButtonType.CANCEL}
                        onClick={onClickCancel}
                        label={labelCancel ?? stringsRes.common.clear}
                    />
                    <ModalButton
                        type={typeConfirm ?? ButtonType.CONFIRM}
                        onClick={onClickConfirm}
                        label={labelConfirm ?? stringsRes.filter.action}
                    />
                </ModalButtonContainer>
                {onExportAction && (
                    <div className={classes.export}>
                        <Space value={1} />
                        <div className={globalClasses.flexRow}>
                            <ExportButton
                                id="exportRides"
                                onClick={onClickExport(false)}
                                label={stringsRes.filter.exportSelection}
                                isLoading={isExporting}
                                noShrink
                            />
                            {user.puppeteer?.uuid && (
                                <ExportButton
                                    id="exportRides"
                                    onClick={onClickExport(true)}
                                    label={stringsRes.filter.exportRealSelection}
                                    isLoading={isExporting}
                                    noShrink
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const useStyle = makeStyles((theme: Theme) =>
    createStyles({
        filterContainer: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            paddingRight: 20,
            '@media (max-width: 700px)': {
                '& p.MuiTypography-root': {
                    display: 'none',
                },
            },
            '& .LCEndContainer': {
                marginTop: 0,
                paddingTop: theme.spacing(2),
                borderTop: `1px solid ${theme.palette.colors.background.separator}`,
                '& .MuiButtonBase-root': {
                    flex: 1,
                    maxWidth: 'unset',
                    '&:first-child': {
                        marginLeft: 0,
                    },
                    '&:last-child': {
                        marginRight: 0,
                    },
                },
            },
        },
        filterInput: {
            marginBottom: 8,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            '& .MuiFormControlLabel-root': {
                marginTop: 8,
                marginLeft: 0,
                marginBottom: 8,
            },
            '& .MuiButtonBase-root': {
                paddingLeft: 0,
            },
            '& .MuiTypography-root': {
                color: theme.palette.colors.text.label,
                fontWeight: 500,
            },
        },
        autoCompleteInput: {
            '& .MuiFilledInput-root': {
                background: theme.palette.colors.background.primaryElement,
                borderRadius: 6,
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
                border: `1.5px solid transparent`,
            },
            '& div.Mui-focused': {
                background: theme.palette.colors.background.secondaryBtn,
                border: `1.5px solid ${theme.palette.secondary.main} !important`,
            },
            '& :before': {
                borderBottomColor: 'transparent!important',
            },
            '& .MuiFilledInput-underline:after': {
                borderBottomColor: 'transparent',
            },
            '& .MuiAutocomplete-endAdornment': {
                right: '16px!important',
            },
            '& .MuiFilledInput-root .MuiFilledInput-input': {
                paddingLeft: 8,
            },
            '& label.Mui-focused': {
                color: theme.palette.secondary.main,
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
                marginLeft: 4,
            },
            '& label': {
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
                marginLeft: 4,
            },
        },
        autoCompletePaper: {
            background: theme.palette.colors.background.appBar,
        },
        icon: {
            width: 20,
            height: 20,
            right: 16,
        },
        dateInterval: {
            display: 'flex',
            flex: 1,
            minWidth: 200,
        },
        multiple: {
            marginBottom: theme.spacing(1),
        },
        filterStickyContainer: {
            position: 'sticky',
            bottom: 0,
            backgroundColor: theme.palette.background.default,
            zIndex: 1,
        },
        export: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
        },
    }),
);

const mapStateToProps = (state) => ({
    isExporting: pendingSelectors.isExportingRides(state),
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(Filter);
